/* eslint-disable no-param-reassign */

import * as React from 'react';
import moment from 'moment-timezone';
import range from 'lodash/range';
import { useSelector } from 'react-redux';
import { skontodefinitionenByArtSelector } from 'store/lookups/selector';
import { useUmsatzSummaryData } from 'reports/umsatzuebersicht/hooks/useUmsatzSummaryData';
import { FilterValues } from '../types';

export interface HochrechnungMonth {
    month: string; // YYYY-MM-DD with DD always 01
    kundenObj: Record<string, boolean>;
    kunden: number;
    kundenSoFar: number;
    kundenPercent: number;
    kundenPercentSoFar: number;
    recordsObj: Record<string, boolean>;
    records: number;
    recordsSoFar: number;
    recordsPercent: number;
    recordsPercentSoFar: number;
    vkNetto: number;
    vkNettoExtrapolated: boolean;
    vkNettoPercent: number;
    vkNettoPercentExtrapolated: boolean;
    vkNettoPercentSoFar: number;
    vkNettoPercentLeft: number;
    vkNettoTotalSoFar: number;
    vkNettoTotalLeft: number;
    nMarge: number;
    nMargeExtrapolated: boolean;
    nMargePercent: number;
    nMargePercentExtrapolated: boolean;
    nMargePercentSoFar: number;
    nMargePercentLeft: number;
    nMargeTotalSoFar: number;
    nMargeTotalLeft: number;
}

export interface HochrechnungData {
    loading: boolean;
    months: HochrechnungMonth[];
    proportionalEndMonth: string;
    kNrList: number[];
    kundenTotal: number;
    kundenTotalOriginal: number;
    kundenProportionalTotal: number;
    recordsTotal: number;
    recordsTotalOriginal: number;
    recordsProportionalTotal: number;
    vkNettoTotal: number;
    vkNettoTotalOriginal: number;
    vkNettoProportionalTotal: number;
    vkNettoTotalLeft: number;
    vkNettoPercentSoFar: number;
    vkNettoPercentLeft: number;
    nMargeTotal: number;
    nMargeTotalOriginal: number;
    nMargeProportionalTotal: number;
    nMargeTotalLeft: number;
    nMargePercentSoFar: number;
    nMargePercentLeft: number;
}

const defaultHochrechnungMonth: HochrechnungMonth = {
    month: '',
    kundenObj: {},
    kunden: 0,
    kundenSoFar: 0,
    kundenPercent: 0,
    kundenPercentSoFar: 0,
    recordsObj: {},
    records: 0,
    recordsSoFar: 0,
    recordsPercent: 0,
    recordsPercentSoFar: 0,
    vkNetto: 0,
    vkNettoExtrapolated: false,
    vkNettoPercent: 0,
    vkNettoPercentExtrapolated: false,
    vkNettoPercentSoFar: 0,
    vkNettoPercentLeft: 0,
    vkNettoTotalSoFar: 0,
    vkNettoTotalLeft: 0,
    nMarge: 0,
    nMargeExtrapolated: false,
    nMargePercent: 0,
    nMargePercentExtrapolated: false,
    nMargePercentSoFar: 0,
    nMargePercentLeft: 0,
    nMargeTotalSoFar: 0,
    nMargeTotalLeft: 0,
};

export const useHochrechnungData = (
    { sourceEntity }: FilterValues,
    datumVon: string,
    priorYear?: HochrechnungData,
): HochrechnungData => {
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);

    let extrapolate = false;

    let datumBis = moment(datumVon).add({ years: 1 }).subtract({ days: 1 });
    const lastOfPriorMonth = moment('2026-04-01').subtract({ months: 1 }).endOf('month');
    if (datumBis.isAfter(lastOfPriorMonth)) {
        datumBis = lastOfPriorMonth;
        extrapolate = true;
    }

    const proportionalEndMonth = extrapolate
        ? lastOfPriorMonth.format('YYYY-MM')
        : lastOfPriorMonth.clone().subtract({ years: 1 }).format('YYYY-MM');

    const umsatzSummaryData = useUmsatzSummaryData({
        source: sourceEntity,
        datumVon,
        datumBis: datumBis.format('YYYY-MM-DD'),
        groupBy: 'kunde',
        sortColumn: '',
        sortDirection: 'asc',
        kundeUmsatzTsdVon: 0,
        kundeUmsatzTsdBis: 0,
        enabled: true,
    });

    return React.useMemo(() => {
        const months: Record<string, HochrechnungMonth> = {};

        range(0, 12).forEach((month) => {
            const firstDayS = moment(datumVon).add({ months: month }).format('YYYY-MM-DD');
            months[firstDayS] = {
                ...defaultHochrechnungMonth,
                kundenObj: {},
                recordsObj: {},
                month: firstDayS,
            };
        });

        let vkNettoTotal = 0;
        let vkNettoProportionalTotal = 0;
        let nMargeTotal = 0;
        let nMargeProportionalTotal = 0;

        const kundenTotalObj: Record<string, boolean> = {};
        const kundenProportionalTotalObj: Record<string, boolean> = {};
        const recordsTotalObj: Record<string, boolean> = {};
        const recordsProportionalTotalObj: Record<string, boolean> = {};

        umsatzSummaryData.recordPositionList.forEach((position) => {
            const record = umsatzSummaryData.recordList.find((r) => r.nr === position.recordNr);

            if (!record) {
                return;
            }

            const month = months[moment(record.datum).startOf('month').format('YYYY-MM-DD')];
            if (!month) {
                return;
            }

            kundenTotalObj[record.kNr.toString()] = true;
            month.kundenObj[record.kNr.toString()] = true;

            recordsTotalObj[record.nr] = true;
            month.recordsObj[record.nr] = true;

            const skontoP = skontodefinitionenByArt[record.zahlBed]?.skP1 || 0;

            const ekNetto = position.posEkPreis * position.posMenge;
            const vkNetto = position.posEPreis * position.posMenge;

            const tippProv = record.tipp === 0 ? 0 : (vkNetto / 100) * record.tipp;
            const skonto = skontoP === 0 ? 0 : (vkNetto / 100) * skontoP;
            const bMarge = vkNetto - ekNetto;
            const nMarge = bMarge - tippProv - skonto - record.fracht - record.roadPr;

            month.vkNetto += vkNetto;
            vkNettoTotal += vkNetto;

            month.nMarge += nMarge;
            nMargeTotal += nMarge;

            if (month.month.substring(0, 7) <= proportionalEndMonth) {
                kundenProportionalTotalObj[record.kNr.toString()] = true;
                recordsProportionalTotalObj[record.nr] = true;
                vkNettoProportionalTotal += vkNetto;
                nMargeProportionalTotal += nMarge;
            }
        });

        let kundenTotal = Object.keys(kundenTotalObj).length;
        const kundenProportionalTotal = Object.keys(kundenProportionalTotalObj).length;
        const kundenSoFarObj: Record<string, boolean> = {};

        let recordsTotal = Object.keys(recordsTotalObj).length;
        const recordsProportionalTotal = Object.keys(recordsProportionalTotalObj).length;
        let recordsSoFar = 0;
        let recordPercentSoFar = 0;

        let vkNettoTotalSoFar = 0;
        let vkNettoTotalLeft = vkNettoTotal;
        let vkNettoPercentSoFar = 0;
        let vkNettoPercentLeft = 100;

        let nMargeTotalSoFar = 0;
        let nMargeTotalLeft = nMargeTotal;
        let nMargePercentSoFar = 0;
        let nMargePercentLeft = 100;

        Object.values(months).forEach((month) => {
            month.kunden = Object.keys(month.kundenObj).length;

            Object.keys(month.kundenObj).forEach((kNr) => {
                kundenSoFarObj[kNr] = true;
            });

            const kundenSoFar = Object.keys(kundenSoFarObj).length;
            month.kundenSoFar += kundenSoFar;
            month.kundenPercent = (month.kunden / kundenTotal) * 100;
            month.kundenPercentSoFar = (kundenSoFar / kundenTotal) * 100;

            month.records = Object.keys(month.recordsObj).length;
            recordsSoFar += month.records;
            month.recordsSoFar += recordsSoFar;
            month.recordsPercent = (month.records / recordsTotal) * 100;
            recordPercentSoFar += month.recordsPercent;
            month.recordsPercentSoFar = recordPercentSoFar;

            vkNettoTotalLeft -= month.vkNetto;
            vkNettoTotalSoFar += month.vkNetto;
            month.vkNettoTotalSoFar = vkNettoTotalSoFar;
            month.vkNettoTotalLeft = vkNettoTotalLeft;
            month.vkNettoPercent = (month.vkNetto / vkNettoTotal) * 100;
            vkNettoPercentSoFar += month.vkNettoPercent;
            month.vkNettoPercentSoFar = vkNettoPercentSoFar;
            vkNettoPercentLeft -= month.vkNettoPercent;
            month.vkNettoPercentLeft = vkNettoPercentLeft;

            nMargeTotalLeft -= month.nMarge;
            nMargeTotalSoFar += month.nMarge;
            month.nMargeTotalSoFar = nMargeTotalSoFar;
            month.nMargeTotalLeft = nMargeTotalLeft;
            month.nMargePercent = (month.nMarge / nMargeTotal) * 100;
            nMargePercentSoFar += month.nMargePercent;
            month.nMargePercentSoFar = nMargePercentSoFar;
            nMargePercentLeft -= month.nMargePercent;
            month.nMargePercentLeft = nMargePercentLeft;
        });

        const kundenTotalOriginal = kundenTotal;
        const recordsTotalOriginal = recordsTotal;
        const vkNettoTotalOriginal = vkNettoTotal;
        const nMargeTotalOriginal = nMargeTotal;

        if (extrapolate && priorYear) {
            const currentMonthM = moment('2026-04-01').startOf('month');

            const extrapolateUntilM = moment(datumVon).add({ years: 1 }).subtract({ days: 1 });

            const lastYearPriorMonthS = currentMonthM.clone().subtract({ years: 1, months: 1 }).format('YYYY-MM-DD');

            const lastYearPriorMonth = priorYear.months.find((m) => m.month === lastYearPriorMonthS);

            const priorMonthS = currentMonthM.clone().subtract({ months: 1 }).format('YYYY-MM-DD');
            const priorMonth = Object.values(months).find((m) => m.month === priorMonthS);

            if (lastYearPriorMonth && priorMonth) {
                const extrapolatedKundenTotal = (priorMonth.kundenSoFar / lastYearPriorMonth.kundenPercentSoFar) * 100;

                const extrapolatedRecordsTotal =
                    (priorMonth.recordsSoFar / lastYearPriorMonth.recordsPercentSoFar) * 100;

                const extrapolatedVkNettoTotal =
                    (priorMonth.vkNettoTotalSoFar / lastYearPriorMonth.vkNettoPercentSoFar) * 100;
                const extrapolatedNMargeTotal =
                    (priorMonth.nMargeTotalSoFar / lastYearPriorMonth.nMargePercentSoFar) * 100;

                do {
                    const currentMonthS = currentMonthM.format('YYYY-MM-DD');
                    const lastYearCurrentMonthS = currentMonthM.clone().subtract({ years: 1 }).format('YYYY-MM-DD');

                    const currentMonth = Object.values(months).find((m) => m.month === currentMonthS);
                    const lastYearCurrentMonth = priorYear.months.find((m) => m.month === lastYearCurrentMonthS);

                    if (currentMonth && lastYearCurrentMonth) {
                        currentMonth.vkNettoExtrapolated = true;
                        currentMonth.vkNetto = extrapolatedVkNettoTotal * (lastYearCurrentMonth.vkNettoPercent / 100);

                        currentMonth.nMargeExtrapolated = true;
                        currentMonth.nMarge = extrapolatedNMargeTotal * (lastYearCurrentMonth.nMargePercent / 100);
                    }

                    currentMonthM.add({ months: 1 });
                } while (currentMonthM.isBefore(extrapolateUntilM));

                kundenTotal = extrapolatedKundenTotal;
                recordsTotal = extrapolatedRecordsTotal;
                vkNettoTotal = extrapolatedVkNettoTotal;
                nMargeTotal = extrapolatedNMargeTotal;
            }
        }

        vkNettoTotalSoFar = 0;
        vkNettoTotalLeft = vkNettoTotal;
        vkNettoPercentSoFar = 0;
        vkNettoPercentLeft = 100;

        nMargeTotalSoFar = 0;
        nMargeTotalLeft = nMargeTotal;
        nMargePercentSoFar = 0;
        nMargePercentLeft = 100;

        Object.values(months).forEach((month) => {
            vkNettoTotalLeft -= month.vkNetto;
            vkNettoTotalSoFar += month.vkNetto;
            month.vkNettoTotalSoFar = vkNettoTotalSoFar;
            month.vkNettoTotalLeft = vkNettoTotalLeft;
            month.vkNettoPercent = (month.vkNetto / vkNettoTotal) * 100;
            vkNettoPercentSoFar += month.vkNettoPercent;
            month.vkNettoPercentSoFar = vkNettoPercentSoFar;
            vkNettoPercentLeft -= month.vkNettoPercent;
            month.vkNettoPercentLeft = vkNettoPercentLeft;

            month.vkNettoPercentExtrapolated = extrapolate;

            nMargeTotalLeft -= month.nMarge;
            nMargeTotalSoFar += month.nMarge;
            month.nMargeTotalSoFar = nMargeTotalSoFar;
            month.nMargeTotalLeft = nMargeTotalLeft;
            month.nMargePercent = (month.nMarge / nMargeTotal) * 100;
            nMargePercentSoFar += month.nMargePercent;
            month.nMargePercentSoFar = nMargePercentSoFar;
            nMargePercentLeft -= month.nMargePercent;
            month.nMargePercentLeft = nMargePercentLeft;

            month.nMargePercentExtrapolated = extrapolate;
        });

        return {
            loading: umsatzSummaryData.isLoading,
            months: Object.values(months),
            proportionalEndMonth,
            kNrList: Object.keys(kundenProportionalTotalObj).map((kNr) => Number.parseInt(kNr, 10) || 0),
            kundenTotal,
            kundenTotalOriginal,
            kundenProportionalTotal,
            recordsProportionalTotal,
            recordsTotal,
            recordsTotalOriginal,
            vkNettoTotal,
            vkNettoTotalOriginal,
            vkNettoProportionalTotal,
            vkNettoTotalLeft,
            vkNettoPercentSoFar,
            vkNettoPercentLeft,
            nMargeTotal,
            nMargeTotalOriginal,
            nMargeProportionalTotal,
            nMargeTotalLeft,
            nMargePercentSoFar,
            nMargePercentLeft,
        };
    }, [
        datumVon,
        extrapolate,
        priorYear,
        proportionalEndMonth,
        skontodefinitionenByArt,
        umsatzSummaryData.isLoading,
        umsatzSummaryData.recordList,
        umsatzSummaryData.recordPositionList,
    ]);
};
